import React, { useState, useEffect, useRef } from 'react';
import { SurveyComponent } from '../../../../models/SurveyTemplates';
import ImagePicker from '../ImagePicker';

interface ImagePickerFieldProps {
    field: SurveyComponent;
    value?: File[] | string[];
    onChange?: (value: File[] | string[]) => void;
    disabled?: boolean;
}

const ImagePickerField: React.FC<ImagePickerFieldProps> = ({
    field,
    value = [],
    onChange,
    disabled = false
}) => {
    const [images, setImages] = useState<File[]>([]);
    const [existingPaths, setExistingPaths] = useState<string[]>([]);
    const isInternalUpdate = useRef(false);

    // Sync with external value changes
    useEffect(() => {
        // Skip if this is an internal update to prevent circular dependency
        if (isInternalUpdate.current) {
            isInternalUpdate.current = false;
            return;
        }

        if (value && Array.isArray(value) && value.length > 0) {
            // Handle mixed arrays - separate Files from strings
            const files: File[] = [];
            const paths: string[] = [];
            
            for (const item of value) {
                if (item instanceof File) {
                    files.push(item);
                } else if (typeof item === 'string') {
                    paths.push(item);
                }
            }
            
            // Only update state if the values have actually changed
            setImages(prevImages => {
                if (prevImages.length !== files.length || 
                    !prevImages.every((img, index) => img === files[index])) {
                    return files;
                }
                return prevImages;
            });
            
            setExistingPaths(prevPaths => {
                if (prevPaths.length !== paths.length || 
                    !prevPaths.every((path, index) => path === paths[index])) {
                    return paths;
                }
                return prevPaths;
            });
        } else {
            // Only update if current state is not empty
            setImages(prevImages => prevImages.length > 0 ? [] : prevImages);
            setExistingPaths(prevPaths => prevPaths.length > 0 ? [] : prevPaths);
        }
    }, [value]);

    const handleImagesChange = (newImages: File[]) => {
        setImages(newImages);
        if (onChange) {
            isInternalUpdate.current = true;
            // Combine existing paths with new images
            // The new images will be uploaded, existing paths will be preserved
            if (existingPaths.length > 0 && newImages.length > 0) {
                // Both existing and new - parent will handle this mixed state
                onChange([...existingPaths, ...newImages] as any);
            } else if (newImages.length > 0) {
                // Only new images
                onChange(newImages);
            } else if (existingPaths.length > 0) {
                // Only existing paths remain
                onChange(existingPaths);
            } else {
                // No images at all
                onChange([]);
            }
        }
    };

    const handleExistingImagesChange = (updatedPaths: string[]) => {
        setExistingPaths(updatedPaths);
        if (onChange) {
            isInternalUpdate.current = true;
            // Combine updated existing paths with any new images
            if (updatedPaths.length > 0 && images.length > 0) {
                onChange([...updatedPaths, ...images] as any);
            } else if (images.length > 0) {
                onChange(images);
            } else if (updatedPaths.length > 0) {
                onChange(updatedPaths);
            } else {
                onChange([]);
            }
        }
    };

    return (
        <ImagePicker
            images={images}
            setImages={handleImagesChange}
            existingImagePaths={existingPaths}
            onExistingImagesChange={handleExistingImagesChange}
        />
    );
};

export default ImagePickerField;
